import type { RealtimeEvent } from '@vdpm/shared';
import { useStore } from '../store/useStore';

let source: EventSource | null = null;
let reconnectTimer: number | null = null;
let refetchTimer: number | null = null;

function scheduleRefetch(): void {
  if (refetchTimer) return;
  refetchTimer = window.setTimeout(() => {
    refetchTimer = null;
    useStore.getState().refetchProjects();
  }, 800);
}

function handleEvent(evt: RealtimeEvent): void {
  if (['project', 'stage', 'invoice', 'document', 'alert'].includes(evt.entity)) {
    scheduleRefetch();
  }
  // Chat messages live outside the project refetch — refresh just that
  // project's thread so open chat views update in real time.
  if (evt.entity === 'message' && evt.projectId) {
    useStore.getState().fetchMessages(evt.projectId).catch(() => undefined);
  }
}

export function startRealtime(): () => void {
  if (source) return stopRealtime;

  let backoffMs = 3_000;
  const open = () => {
    source = new EventSource('/sse', { withCredentials: true });
    source.addEventListener('open', () => {
      // Successful handshake — reset the backoff.
      backoffMs = 3_000;
    });
    source.addEventListener('error', () => {
      // EventSource hides the HTTP status code, but a closed connection
      // immediately after dispatch typically means 401/403. Back off
      // exponentially up to 60 s so we don't hammer the API.
      source?.close();
      source = null;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      reconnectTimer = window.setTimeout(open, backoffMs);
      backoffMs = Math.min(backoffMs * 2, 60_000);
    });
    const onMessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as RealtimeEvent;
        handleEvent(data);
      } catch {
        /* ignore */
      }
    };
    // Backend uses entity-prefixed event names
    ['message', 'invoice', 'stage', 'project', 'alert', 'document'].forEach((entity) => {
      ['created', 'updated', 'deleted'].forEach((action) => {
        source!.addEventListener(`${entity}.${action}`, onMessage as EventListener);
      });
    });
  };

  open();
  return stopRealtime;
}

export function stopRealtime(): void {
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  source?.close();
  source = null;
}
